import { useState, useCallback } from "react";
import { message as antdMessage } from "antd";
import { getReq, postReq } from "../api";

/**
 * useAttendance
 *
 * Mirrors the AttendanceManagement component's expectations:
 *   isClockedIn, punchIn, punchOut, activeShift,
 *   history, month, setMonth, fetchHistory, loading
 *
 * API base: /api/user
 *
 * Notes from controllers:
 *  - punchIn / punchOut require { coordinates: [lng, lat], source: "web" }
 *  - getHistory takes { month: "YYYY-MM" } in body  → returns data[]
 *  - getToday returns a single attendance record or null
 *  - status 200 = success, status 201 = error (non-standard but that's the backend)
 */

const BASE = "/user";

/* ── Location helper ─────────────────────────────────── */
const getCoordinates = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve([pos.coords.longitude, pos.coords.latitude]),
      () => {
        console.warn("Location permission denied, using dummy coordinates");
        resolve([72.5714, 23.0225]); // Dummy coordinates
      },
      // () =>
      //   reject(
      //     new Error(
      //       "Location permission denied. Please allow location access to punch in/out.",
      //     ),
      //   ),
    );
  });

/* ── hook ────────────────────────────────────────────── */
export default function useAttendance() {
  const today = new Date().toISOString().slice(0, 7); // YYYY-MM default

  const [activeShift, setActiveShift] = useState(null); // today's record
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(today);
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────
  // Fetch today's shift — called on mount and after punch
  // ─────────────────────────────────────────────────────
  const fetchToday = useCallback(async () => {
    try {
      const data = await getReq(`${BASE}/getToday`);
      if (data.status === 200 && data.data) {
        setActiveShift(data.data);
        // Clocked in = has loginTime but no logoutTime
        setIsClockedIn(!!data.data.loginTime && !data.data.logoutTime);
      } else {
        setActiveShift(null);
        setIsClockedIn(false);
      }
    } catch {
      // non-critical — silently fail
    }
  }, []);

  // ─────────────────────────────────────────────────────
  // Fetch attendance history for selected month
  // ─────────────────────────────────────────────────────
  const fetchHistory = useCallback(
    async (overrideMonth) => {
      setLoading(true);
      try {
        const data = await postReq(`${BASE}/getHistory`, {
          month: overrideMonth || month, // YYYY-MM
        });

        if (data.status === 200) {
          setHistory(data.data || []);
        } else {
          antdMessage.error(data.message || "Failed to fetch history");
        }
      } catch (err) {
        antdMessage.error(
          err.response?.data?.message || "Failed to fetch history",
        );
      } finally {
        setLoading(false);
      }

      // Also refresh today whenever history is fetched
      await fetchToday();
    },
    [month, fetchToday],
  );

  // ─────────────────────────────────────────────────────
  // Punch In
  // Requires location — prompts browser permission
  // ─────────────────────────────────────────────────────
  const punchIn = useCallback(async () => {
    setLoading(true);
    try {
      let coordinates;
      try {
        coordinates = await getCoordinates();
      } catch (locErr) {
        antdMessage.error(locErr.message);
        return;
      }

      const data = await postReq(`${BASE}/punchIn`, {
        coordinates,
        source: "web",
      });

      if (data.status === 200) {
        setActiveShift(data.data);
        setIsClockedIn(true);
        antdMessage.success("Punched in successfully");
        await fetchHistory();
      } else {
        antdMessage.warning(data.message || "Could not punch in");
      }
    } catch (err) {
      antdMessage.error(err.response?.data?.message || "Punch in failed");
    } finally {
      setLoading(false);
    }
  }, [fetchHistory]);

  // ─────────────────────────────────────────────────────
  // Punch Out
  // ─────────────────────────────────────────────────────
  const punchOut = useCallback(async () => {
    setLoading(true);
    try {
      let coordinates;
      try {
        coordinates = await getCoordinates();
      } catch (locErr) {
        antdMessage.error(locErr.message);
        return;
      }

      const data = await postReq(`${BASE}/punchOut`, {
        coordinates,
        source: "web",
      });

      if (data.status === 200) {
        setActiveShift(data.data);
        setIsClockedIn(false);
        antdMessage.success("Punched out successfully");
        await fetchHistory();
      } else {
        antdMessage.warning(data.message || "Could not punch out");
      }
    } catch (err) {
      antdMessage.error(err.response?.data?.message || "Punch out failed");
    } finally {
      setLoading(false);
    }
  }, [fetchHistory]);

  // ─────────────────────────────────────────────────────
  // Handle month change — refetch history for new month
  // ─────────────────────────────────────────────────────
  const handleSetMonth = useCallback(
    (val) => {
      setMonth(val);
      fetchHistory(val); // pass directly — state update is async
    },
    [fetchHistory],
  );

  return {
    // state
    isClockedIn,
    activeShift,
    history,
    month,
    loading,
    // actions
    punchIn,
    punchOut,
    fetchHistory,
    fetchToday,
    setMonth: handleSetMonth,
  };
}
